import React, {useState, useMemo, useCallback} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Flex, HStack, IconButton, SimpleGrid, useBreakpointValue} from '@chakra-ui/react'
import ProductTile, {Skeleton as ProductTileSkeleton} from '@app/components/product-tile'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import Carousel from '@app/components/carousel'
import {ChevronRightIcon, ChevronLeftIcon} from '@app/components/custom-icons/index.jsx'
import {useToast} from '@app/hooks/use-toast'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer.js'
import breakpoints from '@app/theme/foundations/breakpoints'
import ShopLookDrawer from '../product-view/ShopLookDrawer'
import ProductScroller from '../product-scroller'

/**
 * CompleteTheLook is a React functional component that renders a ShopLookDrawer component.
 * It is designed to display a curated selection of products that complement a given product,
 * encouraging users to "complete the look."
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.product - The main product to be featured in the "Complete the Look" section.
 *
 * @returns {JSX.Element} A JSX element representing the ShopLookDrawer with the specified product and UI configurations.
 *
 * @component
 * @example
 * const product = { id: 1, name: 'Stylish Jacket' };
 * return <CompleteTheLook product={product} />;
 */
const CompleteTheLook = ({product}) => {
    const intl = useIntl()
    return (
        <ShopLookDrawer
            products={[product]}
            mainTitle={intl.formatMessage({
                id: 'product_detail.recommended_products.title.complete_set',
                defaultMessage: 'Complete the look'
            })}
            buttonProps={{
                styles: 'completeLookDrawerButton',
                text: (
                    <FormattedMessage
                        id="product_set_drawer.header.quick_shop"
                        defaultMessage="Quick shop"
                    />
                )
            }}
        />
    )
}

CompleteTheLook.propTypes = {
    product: PropTypes.object
}

/**
 * ProductCarousel is a React component that renders a carousel of product tiles with various display options.
 *
 * This component provides a flexible and interactive way to showcase products in a carousel format. It supports
 * features such as displaying product images, names, prices, and color variations. Users can navigate through
 * the carousel using next and previous buttons, and they can also toggle products in and out of a wishlist.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the carousel.
 * @param {number|Object} props.nbSlidesToShow - The number of slides to show in the carousel. This can be a fixed
 *                                               number or an object specifying different numbers for various breakpoints.
 * @param {boolean} [props.showProductImages=true] - Whether to display product images.
 * @param {boolean} [props.showProductNames=true] - Whether to display product names.
 * @param {boolean} [props.showProductPrices=true] - Whether to display product prices.
 * @param {boolean} [props.showProductColorVariations=true] - Whether to display product color variations.
 * @param {Array} props.products - An array of product objects to be displayed in the carousel.
 * @param {boolean} props.isLoading - Indicates if the product data is still loading.
 * @param {boolean} [props.isDisabled=false] - Disables carousel interactions if set to true.
 * @param {boolean} [props.isSwitchedImages=false] - Toggles between different product images.
 * @param {Object} props.headerProps - Additional properties for the header section of the carousel.
 * @param {boolean} props.isCompleteTheLook - Indicates if the "Complete The Look" feature is enabled.
 * @param {Object} props.props - Additional properties passed to the Box component wrapping the carousel.
 *
 * @returns {JSX.Element} A React component that renders a product carousel.
 *
 * @example
 * <ProductCarousel
 *   title="Featured Products"
 *   nbSlidesToShow={3}
 *   products={productList}
 *   isLoading={false}
 * />
 */
export const ProductCarousel = ({
    title,
    nbSlidesToShow,
    showProductImages = true,
    showProductNames = true,
    showProductPrices = true,
    showProductColorVariations = true,
    products,
    isLoading,
    isDisabled = false,
    isSwitchedImages = false,
    headerProps,
    isCompleteTheLook,
    isSlider,
    recommender,
    ...props
}) => {
    const [swiper, setSwiper] = useState()
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer

    const isMobile = useBreakpointValue({base: true, md: false})
    const nbSlides = useBreakpointValue(
        typeof nbSlidesToShow === 'object' ? nbSlidesToShow : {base: nbSlidesToShow}
    )

    const carouselSettings = useMemo(
        () => ({
            dots: false,
            arrows: false,
            infinite: true,
            speed: 500,
            slidesToShow: nbSlides || 4,
            swipe: !isDisabled
        }),
        [nbSlides, isMobile, isDisabled]
    )

    const [isProductTileHover, setIsProductTileHover] = useState(false)

    /**
     * Toggles the wishlist status of a given product for a customer.
     *
     * This function checks if the specified product is already present in the customer's wishlist.
     * If the product is found, it removes the product from the wishlist. Otherwise, it adds the product
     * to the wishlist. The function utilizes asynchronous operations to handle the addition or removal
     * of the product from the wishlist.
     *
     * @function
     * @param {Object} product - The product object to be toggled in the wishlist.
     * @param {string} product.id - The unique identifier of the product.
     *
     * @returns {Promise<void>} A promise that resolves when the wishlist operation is complete.
     *
     * @dependencies
     * - wishlist: The current state of the customer's wishlist.
     * - customerId: The unique identifier of the customer.
     * - removeProductFromWishlist: Function to remove an item from the wishlist.
     * - addProductToWishlist: Function to add an item to the wishlist.
     * - deleteCustomerProductListItem: Function to delete a product list item from the customer's wishlist.
     * - createCustomerProductListItem: Function to create a product list item in the customer's wishlist.
     * - toast: Function to display notifications to the user.
     * - formatMessage: Function to format messages for display.
     */
    const handleWishlistToggle = useCallback(
        async (product) => {
            if (
                wishlist?.customerProductListItems?.some((item) => item.productId === product?.id)
            ) {
                await removeProductFromWishlist(product)
            } else {
                await addProductToWishlist(product)
            }
        },
        [
            wishlist,
            customerId,
            removeProductFromWishlist,
            addProductToWishlist,
            deleteCustomerProductListItem,
            createCustomerProductListItem,
            toast,
            formatMessage
        ]
    )

    /**
     * Calculates the widths of slides to be displayed in a carousel based on the number of slides to show.
     * Utilizes the `useMemo` hook to optimize performance by memoizing the calculated widths.
     *
     * @param {number|Object} nbSlidesToShow - The number of slides to display at once.
     *        If a number is provided, it represents the fixed number of slides to show.
     *        If an object is provided, it should map breakpoints to the number of slides to show at each breakpoint.
     *
     * @returns {Array<string>} An array of strings representing the widths in viewport width units ('vw').
     *        If `nbSlidesToShow` is not provided, returns an array with a single element '100vw'.
     *        If `nbSlidesToShow` is an object, calculates widths for each breakpoint based on the provided values.
     *        If `nbSlidesToShow` is a number, calculates a single width value.
     */
    const calculateWidths = useMemo(
        () => (nbSlidesToShow) => {
            if (!nbSlidesToShow) {
                return ['100vw']
            }

            if (typeof nbSlidesToShow === 'object') {
                const breakpointsKeys = Object.keys(breakpoints)
                let lastDefinedValue = null

                return breakpointsKeys.reduce((acc, breakpoint) => {
                    if (nbSlidesToShow[breakpoint] !== undefined) {
                        lastDefinedValue = `${Math.round(100 / nbSlidesToShow[breakpoint])}vw`
                    }
                    if (lastDefinedValue) {
                        acc.push(lastDefinedValue)
                    }
                    return acc
                }, [])
            }

            const width = `${Math.round(100 / nbSlidesToShow)}vw`
            return [width]
        },
        [nbSlidesToShow]
    )

    const renderProductTile = (product, isCompleteTheLook, onClick) => (
        <>
            <ProductTile
                product={product}
                isSwitchedImages={isSwitchedImages}
                isFavourite={wishlist?.customerProductListItems?.some(
                    (item) => item.productId === product?.id
                )}
                onFavouriteToggle={() => {
                    handleWishlistToggle(product)
                }}
                onClick={() => onClick(product)}
                dynamicImageProps={{
                    widths: calculateWidths(nbSlidesToShow),
                    imageProps: {
                        width: '100%'
                    }
                }}
                showProductImages={showProductImages}
                showProductNames={showProductNames}
                showProductPrices={showProductPrices}
                showProductColorVariations={showProductColorVariations}
                isFavIconInTitle={true}
                isPriceLarge={false}
                mt={5}
                recommender={recommender}
            />
            {isCompleteTheLook && (isProductTileHover === product.id || isMobile) && (
                <CompleteTheLook product={product} />
            )}
        </>
    )

    return (
        <Box css={[ProductCarousel.css]} {...props}>
            <Flex
                flex={1}
                pt={3}
                pb={['35px', null, '29px']}
                mx={['10px', null, '20px']}
                justifyContent={'space-between'}
                alignItems={'flex-end'}
                {...headerProps}
            >
                {title}
                {!isDisabled && products?.length > nbSlides && !isSlider && (
                    <HStack display={isMobile ? 'none' : 'flex'} gap={6}>
                        <IconButton
                            aria-label={formatMessage({
                                id: 'carousel.button.prev',
                                defaultMessage: 'Previous'
                            })}
                            icon={<ChevronLeftIcon boxSize="12px" />}
                            variant="ghost"
                            onClick={() => {
                                swiper?.slickPrev()
                            }}
                            w={'40px'}
                            h={'40px'}
                            border={'1px solid var(--chakra-colors-gray)'}
                            _focus={{zIndex: 1}}
                        />
                        <IconButton
                            aria-label={formatMessage({
                                id: 'carousel.button.next',
                                defaultMessage: 'Next'
                            })}
                            icon={<ChevronRightIcon boxSize="12px" />}
                            variant="ghost"
                            onClick={() => {
                                swiper?.slickNext()
                            }}
                            w={'40px'}
                            h={'40px'}
                            border={'1px solid var(--chakra-colors-gray)'}
                        />
                    </HStack>
                )}
            </Flex>
            {products?.length <= nbSlides ? (
                <SimpleGrid columns={nbSlides} spacing={10} px={[10, null, 20]} py={1} as="ul">
                    {products?.map((product, idx) => (
                        <Box
                            key={product?.id || idx}
                            width="100%"
                            as="li"
                            listStyleType="none"
                            position={'relative'}
                            onMouseEnter={() => setIsProductTileHover(product.id)}
                            onMouseLeave={() => setIsProductTileHover(false)}
                        >
                            {renderProductTile(product, isCompleteTheLook, props.onClick)}
                        </Box>
                    ))}
                </SimpleGrid>
            ) : isSlider ? (
                <ProductScroller
                    products={products}
                    isLoading={isLoading}
                    recommender={recommender ? recommender : null}
                />
            ) : (
                <Carousel
                    settings={carouselSettings}
                    label={formatMessage({
                        id: 'home.services.push.carousel.label',
                        defaultMessage: 'Home services push carousel'
                    })}
                    setSwiper={setSwiper}
                    nbSlidesToShowByBreakpoint={nbSlidesToShow}
                >
                    {isLoading
                        ? new Array(6)
                              .fill(0)
                              .map((_, index) => (
                                  <ProductTileSkeleton key={`skeleton-${index}`} p={5} />
                              ))
                        : products?.map((product) => (
                              <Box
                                  key={product.id}
                                  position={'relative'}
                                  onMouseEnter={() => setIsProductTileHover(product.id)}
                                  onMouseLeave={() => setIsProductTileHover(false)}
                              >
                                  {renderProductTile(product, isCompleteTheLook, props.onClick)}
                              </Box>
                          ))}
                </Carousel>
            )}
        </Box>
    )
}

ProductCarousel.css = css`
    .slick-slider {
        padding-right: min(calc(44px), calc(100vw / 8));

        @media screen and (min-width: 768px) {
            padding-right: 0;
            margin-left: 15px;
            margin-right: 15px;
        }
    }
    .slick-track {
        display: flex;
    }
    .slick-list {
        overflow: visible;

        @media screen and (min-width: 768px) {
            overflow: hidden;
        }
    }
    .slick-slide {
        height: inherit;
        margin-left: 10px;

        @media screen and (min-width: 768px) {
            margin-left: 5px;
            margin-right: 5px;
        }
    }
`

ProductCarousel.propTypes = {
    title: PropTypes.object,
    nbSlidesToShow: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    showProductImages: PropTypes.bool,
    showProductNames: PropTypes.bool,
    showProductPrices: PropTypes.bool,
    showProductColorVariations: PropTypes.bool,
    products: PropTypes.array,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isSwitchedImages: PropTypes.bool,
    isSlider: PropTypes.bool,
    headerProps: PropTypes.object,
    isCompleteTheLook: PropTypes.bool,
    onClick: PropTypes.func,
    recommender: PropTypes.string
}
